import { CustomerKeyType } from '../../../constants';
import { wishlistAPIClient } from '../wishlistAPIClient';
import { WISHLIST_QUERY_KEYS } from './keys';
import type { GetWishlistRequest, GetWishlistResponse } from './types';

import type { QueryClient } from '@tanstack/react-query';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { mutationOptions } from '../../common/utils';
import { setWishlistSku } from '../../../utils';

const getWishlistQueryKey = (customerKey?: string, customerKeyType?: CustomerKeyType) =>
  WISHLIST_QUERY_KEYS.list({ customerKey, customerKeyType });

const cancelAndGetPreviousData = async (queryClient: QueryClient, queryKey: readonly unknown[]) => {
  await queryClient.cancelQueries({ queryKey });
  return queryClient.getQueryData(queryKey);
};

export const WISHLIST_QUERIES = {
  getWishlist: (request: GetWishlistRequest) => {
    return queryOptions({
      queryKey: WISHLIST_QUERY_KEYS.list(request),
      queryFn: () => wishlistAPIClient.getWishlist(request),
      placeholderData: keepPreviousData,
    });
  },

  addProductToWishlist: (queryClient: QueryClient) =>
    mutationOptions({
      mutationKey: WISHLIST_QUERY_KEYS.addProductToWishlist(),
      mutationFn: wishlistAPIClient.addProductToWishlist,
      onSuccess: (data, variables) => {
        const queryKey = getWishlistQueryKey(variables.customerKey, variables.customerKeyType);
        setWishlistSku(data.wishlist.skus);
        queryClient.setQueryData(queryKey, data.wishlist);
      },
      onMutate: async (payload) => {
        const queryKey = getWishlistQueryKey(payload.customerKey, payload.customerKeyType);
        const previousResponse = await cancelAndGetPreviousData(queryClient, queryKey);

        queryClient.setQueryData(queryKey, (old: GetWishlistResponse) => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            skus: [...old.skus, payload.sku],
          };
        });

        return { previousResponse };
      },
      onError: async (_error, variables, context) => {
        const queryKey = getWishlistQueryKey(variables.customerKey, variables.customerKeyType);
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(queryKey, context?.previousResponse);
      },
    }),

  removeProductFromWishlist: (queryClient: QueryClient) =>
    mutationOptions({
      mutationKey: WISHLIST_QUERY_KEYS.removeProductFromWishlist(),
      mutationFn: wishlistAPIClient.removeProductFromWishlist,
      onSuccess: (data, variables) => {
        const queryKey = getWishlistQueryKey(variables.customerKey, variables.customerKeyType);
        setWishlistSku(data.wishlist.skus);
        queryClient.setQueryData(queryKey, data.wishlist);
      },
      onMutate: async (payload) => {
        const queryKey = getWishlistQueryKey(payload.customerKey, payload.customerKeyType);
        const previousResponse = await cancelAndGetPreviousData(queryClient, queryKey);

        queryClient.setQueryData(queryKey, (old: GetWishlistResponse) => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            skus: old.skus.filter((sku) => sku !== payload.sku),
          };
        });

        return { previousResponse };
      },
      onError: async (_error, variables, context) => {
        const queryKey = getWishlistQueryKey(variables.customerKey, variables.customerKeyType);
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(queryKey, context?.previousResponse);
      },
    }),
};
